import { Command } from 'commander';
import chalk from 'chalk';
import inquirer from 'inquirer';
import fs from 'fs-extra';
import { join, isAbsolute, basename, dirname } from 'path';
import { execSync, spawnSync } from 'child_process';
import { logger } from '../lib/logger.js';
import { installRalphConfig } from '../lib/installer.js';
import { I18N_NOOP_TEMPLATE } from '../lib/i18n-noop-template.js';
import { buildInitialAppSettings } from '../lib/config-sync.js';
import {
  type InitState,
  loadInitState,
  saveInitState,
  createFreshInitState,
  executeStep,
  safeWriteFile,
  findKnownHash,
  recordFile,
  logSafeWriteResult,
  hasIncompleteSteps,
  displayResumeSummary,
  computeHash,
} from '../lib/file-tracker.js';

// Get templates directory path
// __dirname is 'dist' after bundling, so go up one level to get to package root
const TEMPLATES_DIR = join(dirname(__dirname), 'templates', 'project');

interface InitOptions {
  dryRun?: boolean;
  yes?: boolean;
  preview?: boolean;
  here?: boolean;
}

interface ProjectConfig {
  name: string;
  nameLower: string;
  database: 'sqlserver';
  preview: boolean;
  projectDir?: string; // Explicit project directory (for in-place mode)
  smartStackVersion?: string; // Resolved SmartStack NuGet version (fetched at init time)
  // Multi-tenant is always enabled (personal tenant) — no configuration needed
  email: {
    provider: 'Development' | 'Mailgun' | 'AzureAcs' | 'Disabled';
    setupMailPit?: boolean;
  };
}

function checkPrerequisites(): { dotnet: boolean; dotnetVersion: string | null } {
  try {
    const result = spawnSync('dotnet', ['--version'], { encoding: 'utf-8', shell: true, timeout: 10000 });
    if (result.status === 0 && result.stdout) {
      return { dotnet: true, dotnetVersion: result.stdout.trim() };
    }
    return { dotnet: false, dotnetVersion: null };
  } catch {
    return { dotnet: false, dotnetVersion: null };
  }
}

function isDockerAvailable(): boolean {
  try {
    const result = spawnSync('docker', ['info'], { encoding: 'utf-8', shell: true, timeout: 10000, stdio: 'pipe' });
    return result.status === 0;
  } catch {
    return false;
  }
}

async function setupMailPitContainer(): Promise<void> {
  // Check if container already exists
  const check = spawnSync('docker', ['ps', '-a', '--filter', 'name=^mailpit$', '--format', '{{.Status}}'], {
    encoding: 'utf-8', shell: true, timeout: 10000,
  });

  if (check.stdout?.trim()) {
    const status = check.stdout.trim();
    if (status.startsWith('Up')) {
      logger.success(`MailPit is already running`);
    } else {
      // Container exists but stopped - start it
      logger.info('Starting existing MailPit container...');
      spawnSync('docker', ['start', 'mailpit'], { encoding: 'utf-8', shell: true, timeout: 15000 });
      logger.success('MailPit started');
    }
  } else {
    // Create and start new container
    logger.info('Starting MailPit container...');
    const run = spawnSync('docker', [
      'run', '-d',
      '--name', 'mailpit',
      '--restart', 'unless-stopped',
      '-p', '8025:8025',
      '-p', '1025:1025',
      'axllent/mailpit',
    ], { encoding: 'utf-8', shell: true, timeout: 60000 });

    if (run.status === 0) {
      logger.success('MailPit started successfully');
    } else {
      logger.warning(`Failed to start MailPit: ${run.stderr?.trim() || 'unknown error'}`);
      logger.info('You can start it manually later:');
      logger.info(chalk.cyan('  docker run -d --name mailpit --restart unless-stopped -p 8025:8025 -p 1025:1025 axllent/mailpit'));
      return;
    }
  }

  logger.info(`SMTP server: ${chalk.cyan('localhost:1025')}`);
  logger.info(`Web interface: ${chalk.cyan('http://localhost:8025')}`);
}

/**
 * Converts a name to PascalCase.
 * Examples: 'ba-006' → 'Ba006', 'my-project' → 'MyProject', 'ba_006' → 'Ba006'
 */
function toPascalCase(name: string): string {
  return name
    .split(/[-_.\s]+/)
    .filter(Boolean)
    .map(part => {
      if (/^\d+$/.test(part)) return part;
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join('');
}

/**
 * Validates that a project name is valid for C# namespaces.
 * C# namespace rules:
 * - Must start with a letter or underscore
 * - Can only contain letters, digits, and underscores
 * - Cannot be a C# reserved keyword
 * - Must be PascalCase
 */
function validateCSharpNamespace(name: string): { valid: boolean; error?: string; suggestion?: string } {
  // Check if empty
  if (!name || name.trim().length === 0) {
    return { valid: false, error: 'Project name cannot be empty' };
  }

  // Check first character (must be letter or underscore)
  const firstChar = name[0];
  if (!/^[a-zA-Z_]$/.test(firstChar)) {
    if (/^[0-9]$/.test(firstChar)) {
      return {
        valid: false,
        error: `Project name cannot start with a digit: '${firstChar}'`,
        suggestion: toPascalCase('_' + name),
      };
    }
    return {
      valid: false,
      error: `Project name cannot start with '${firstChar}'`,
      suggestion: toPascalCase(name),
    };
  }

  // Check for invalid characters (anything not letter, digit, or underscore)
  const invalidChars = name.match(/[^a-zA-Z0-9_]/g);
  if (invalidChars) {
    const uniqueInvalid = [...new Set(invalidChars)];
    return {
      valid: false,
      error: `Project name contains invalid characters for C# namespace: ${uniqueInvalid.map(c => `'${c}'`).join(', ')}`,
      suggestion: toPascalCase(name),
    };
  }

  // Check for C# reserved keywords (basic list)
  const csharpKeywords = [
    'abstract', 'as', 'base', 'bool', 'break', 'byte', 'case', 'catch', 'char', 'checked',
    'class', 'const', 'continue', 'decimal', 'default', 'delegate', 'do', 'double', 'else',
    'enum', 'event', 'explicit', 'extern', 'false', 'finally', 'fixed', 'float', 'for',
    'foreach', 'goto', 'if', 'implicit', 'in', 'int', 'interface', 'internal', 'is', 'lock',
    'long', 'namespace', 'new', 'null', 'object', 'operator', 'out', 'override', 'params',
    'private', 'protected', 'public', 'readonly', 'ref', 'return', 'sbyte', 'sealed',
    'short', 'sizeof', 'stackalloc', 'static', 'string', 'struct', 'switch', 'this',
    'throw', 'true', 'try', 'typeof', 'uint', 'ulong', 'unchecked', 'unsafe', 'ushort',
    'using', 'virtual', 'void', 'volatile', 'while',
  ];

  if (csharpKeywords.includes(name.toLowerCase())) {
    return {
      valid: false,
      error: `'${name}' is a C# reserved keyword`,
      suggestion: `${name}_Project`,
    };
  }

  // Check PascalCase
  const pascalVersion = toPascalCase(name);
  if (pascalVersion !== name) {
    return {
      valid: false,
      error: `Project name "${name}" is not PascalCase`,
      suggestion: pascalVersion,
    };
  }

  return { valid: true };
}

interface ProjectContext {
  projectName: string;
  projectDir: string;
  inPlace: boolean;
}

/**
 * Detects project context when name is not provided.
 * Handles GitFlow worktree scenarios and current folder initialization.
 */
async function detectProjectContext(
  name: string | undefined,
  here: boolean
): Promise<ProjectContext> {
  const cwd = process.cwd();

  // If name provided (non-empty) and not --here, check if it's a new directory or in-place
  if (name && name.trim() && !here) {
    const projectDir = isAbsolute(name) ? name : join(cwd, name);

    // If resolved path equals cwd, treat as in-place (user passed "." or current folder name)
    if (projectDir === cwd || basename(cwd) === name) {
      // Fall through to in-place detection below
    } else {
      const projectName = basename(name);
      return { projectName, projectDir, inPlace: false };
    }
  }

  // In-place initialization: detect project name from context
  const parentDir = dirname(cwd);
  const currentFolderName = basename(cwd);
  let detectedName: string | undefined;

  // Check 1: GitFlow config in current folder
  const gitflowConfigPath = join(cwd, '.claude', 'gitflow', 'config.json');
  if (await fs.pathExists(gitflowConfigPath)) {
    try {
      const config = await fs.readJson(gitflowConfigPath);
      if (config.repository?.name) {
        detectedName = config.repository.name;
      }
    } catch {
      // Ignore parse errors
    }
  }

  // Check 2: GitFlow worktree pattern (02-Develop, develop folder with parent having .bare/)
  if (!detectedName) {
    const bareDir = join(parentDir, '.bare');
    if (await fs.pathExists(bareDir)) {
      // We're in a GitFlow worktree, use parent folder name as project
      detectedName = basename(parentDir);
    }
  }

  // Check 3: Use current folder name (fallback)
  if (!detectedName) {
    // If folder is named like "02-Develop" or "develop", try parent
    const worktreePatterns = /^(\d+-)?(?:main|master|develop|development)$/i;
    if (worktreePatterns.test(currentFolderName)) {
      detectedName = basename(parentDir);
    } else {
      detectedName = currentFolderName;
    }
  }

  return {
    projectName: detectedName,
    projectDir: cwd,
    inPlace: true,
  };
}

interface ValidateInPlaceResult {
  error?: string;
  isResume: boolean;
  existingState?: InitState;
}

/**
 * Checks if a directory is suitable for in-place SmartStack initialization.
 * Supports resume: if an incomplete init-state.json exists, returns isResume: true.
 */
async function validateInPlaceDirectory(projectDir: string): Promise<ValidateInPlaceResult> {
  const hasConfig = await fs.pathExists(join(projectDir, '.smartstack', 'config.json'));
  const existingState = await loadInitState(projectDir);

  // Check for incomplete init state (resume scenario)
  if (existingState && hasIncompleteSteps(existingState)) {
    return { isResume: true, existingState };
  }

  // Check for existing SmartStack project (fully initialized)
  if (hasConfig) {
    return { error: 'SmartStack is already initialized in this directory. Use "ss update" to update.', isResume: false };
  }

  // Check for existing .NET solution (without init state = not a SmartStack partial init)
  const files = await fs.readdir(projectDir);
  const hasSln = files.some(f => f.endsWith('.sln'));
  if (hasSln) {
    return { error: 'A .NET solution already exists in this directory. Cannot initialize SmartStack here.', isResume: false };
  }

  // Check for existing src folder with .NET projects
  const srcDir = join(projectDir, 'src');
  if (await fs.pathExists(srcDir)) {
    const srcFiles = await fs.readdir(srcDir);
    const hasCsproj = srcFiles.some(f => f.endsWith('.csproj') || (fs.statSync(join(srcDir, f)).isDirectory()));
    if (hasCsproj) {
      return { error: 'A src/ directory with projects already exists. Cannot initialize SmartStack here.', isResume: false };
    }
  }

  return { isResume: false };
}

function execCommand(command: string, cwd?: string, dryRun = false): void {
  if (dryRun) {
    logger.info(`[DRY RUN] ${command}`);
    return;
  }

  try {
    execSync(command, { cwd, stdio: 'inherit' });
  } catch (error) {
    throw new Error(`Command failed: ${command}`);
  }
}

/**
 * Fetches the latest version of a NuGet package from nuget.org.
 * @param packageName The package name (e.g., 'SmartStack')
 * @param prerelease If true, includes prerelease versions
 * @returns The latest version string (e.g., '2.0.0-preview.380')
 */
async function getLatestNuGetVersion(packageName: string, prerelease: boolean): Promise<string | null> {
  try {
    const response = await fetch(`https://api.nuget.org/v3-flatcontainer/${packageName.toLowerCase()}/index.json`);

    if (!response.ok) {
      logger.warning(`Could not fetch versions for ${packageName} from NuGet`);
      return null;
    }

    const data = await response.json() as { versions: string[] };
    const versions = data.versions;

    if (!versions || versions.length === 0) {
      return null;
    }

    // Filter versions based on prerelease flag
    const filteredVersions = prerelease
      ? versions.filter(v => v.includes('-')) // Prerelease versions contain '-'
      : versions.filter(v => !v.includes('-')); // Stable versions don't contain '-'

    if (filteredVersions.length === 0) {
      return null;
    }

    // Return the last version (versions are sorted by NuGet API)
    return filteredVersions[filteredVersions.length - 1];
  } catch (error) {
    logger.warning(`Error fetching latest version for ${packageName}: ${error instanceof Error ? error.message : error}`);
    return null;
  }
}

/**
 * Loads a template file and replaces placeholders.
 * @param templateName The template file name (e.g., 'ExtensionsDbContext.cs.template')
 * @param projectName The project name to replace {{ProjectName}} with
 * @returns The template content with placeholders replaced
 */
async function loadTemplate(templateName: string, projectName: string): Promise<string> {
  const templatePath = join(TEMPLATES_DIR, templateName);

  if (!await fs.pathExists(templatePath)) {
    throw new Error(`Template not found: ${templatePath}`);
  }

  let content = await fs.readFile(templatePath, 'utf-8');

  // Replace all occurrences of {{ProjectName}} and {{ProjectNameLower}} with the actual project name
  content = content.replace(/\{\{ProjectName\}\}/g, projectName);
  content = content.replace(/\{\{ProjectNameLower\}\}/g, projectName.toLowerCase());

  return content;
}

/**
 * Creates the Dual-DbContext structure files from templates.
 * This includes ExtensionsDbContext, DependencyInjection, and design-time factory.
 */
async function createDualDbContextStructure(
  projectDir: string,
  projectName: string,
  state: InitState,
  dryRun: boolean
): Promise<void> {
  if (dryRun) {
    logger.info('[DRY RUN] Would create Dual-DbContext structure');
    return;
  }

  const srcDir = join(projectDir, 'src');

  // Create directory structure
  const directories = [
    join(srcDir, `${projectName}.Application`, 'Common', 'Interfaces'),
    join(srcDir, `${projectName}.Infrastructure`, 'Persistence'),
    join(srcDir, `${projectName}.Infrastructure`, 'Persistence', 'Configurations'),
    join(srcDir, `${projectName}.Infrastructure`, 'Services'),
    join(srcDir, `${projectName}.Domain`, 'Common'),
    join(srcDir, `${projectName}.Domain`, 'Entities'),
  ];

  for (const dir of directories) {
    await fs.ensureDir(dir);
  }

  // Load and write templates
  const templateFiles: Array<{ template: string; destination: string }> = [
    {
      // Domain shims consumed by every scaffolded entity: soft-delete + domain
      // events base (the package's BaseEntity ships without them) and the
      // error-code DomainException. Without these, scaffold-entity output
      // cannot compile — historically each ba-develop run auto-healed them
      // per-project; shipping them makes the contract deterministic.
      template: 'ExtensionBaseEntity.cs.template',
      destination: join(srcDir, `${projectName}.Domain`, 'Common', 'ExtensionBaseEntity.cs'),
    },
    {
      template: 'DomainException.cs.template',
      destination: join(srcDir, `${projectName}.Domain`, 'Common', 'DomainException.cs'),
    },
    {
      // Local schema constants — scaffolded EF configurations reference
      // SchemaConstants.Extensions via the enclosing namespace.
      template: 'SchemaConstants.cs.template',
      destination: join(srcDir, `${projectName}.Infrastructure`, 'Persistence', 'SchemaConstants.cs'),
    },
    {
      template: 'IExtensionsDbContext.cs.template',
      destination: join(srcDir, `${projectName}.Application`, 'Common', 'Interfaces', 'IExtensionsDbContext.cs'),
    },
    {
      template: 'DependencyInjection.Application.cs.template',
      destination: join(srcDir, `${projectName}.Application`, 'DependencyInjection.cs'),
    },
    {
      template: 'ExtensionsDbContext.cs.template',
      destination: join(srcDir, `${projectName}.Infrastructure`, 'Persistence', 'ExtensionsDbContext.cs'),
    },
    {
      template: 'DesignTimeExtensionsDbContextFactory.cs.template',
      destination: join(srcDir, `${projectName}.Infrastructure`, 'Persistence', 'DesignTimeExtensionsDbContextFactory.cs'),
    },
    {
      // In the Api (STARTUP) project on purpose: CoreDbContext lives in the SmartStack
      // package and EF's design-time factory scan only covers the startup assembly.
      // Required so the deploy pipeline can build the Core migration bundle.
      template: 'DesignTimeCoreDbContextFactory.cs.template',
      destination: join(srcDir, `${projectName}.Api`, 'Persistence', 'DesignTimeCoreDbContextFactory.cs'),
    },
    {
      template: 'DependencyInjection.Infrastructure.cs.template',
      destination: join(srcDir, `${projectName}.Infrastructure`, 'DependencyInjection.cs'),
    },
  ];

  for (const { template, destination } of templateFiles) {
    try {
      const content = await loadTemplate(template, projectName);
      const relPath = destination.replace(projectDir + '\\', '').replace(projectDir + '/', '').replace(/\\/g, '/');
      const result = await safeWriteFile(destination, content, findKnownHash(state, 'backend', relPath));
      logSafeWriteResult(relPath, result);
      recordFile(state, 'backend', relPath, result.hash);
    } catch (error) {
      logger.warning(`Could not create ${template}: ${error instanceof Error ? error.message : error}`);
    }
  }

  logger.success('Dual-DbContext structure created');
}

async function createBackendStructure(config: ProjectConfig, state: InitState, dryRun: boolean): Promise<void> {
  const { name } = config;

  // Handle absolute paths correctly - use explicit projectDir if provided (in-place mode)
  const projectDir = config.projectDir ?? (isAbsolute(name) ? name : join(process.cwd(), name));
  const projectName = basename(name); // Extract just the name from path
  const srcDir = join(projectDir, 'src');
  const slnPath = join(projectDir, `${projectName}.sln`);

  logger.info('Creating .NET solution structure...');

  // Create solution - skip if already exists (resume scenario)
  if (dryRun || !await fs.pathExists(slnPath)) {
    execCommand(`dotnet new sln -n "${projectName}" -o "${projectDir}"`, undefined, dryRun);
  } else {
    logger.info(chalk.gray('Solution already exists, skipping creation'));
  }

  // Convert .slnx to .sln for VS Code compatibility (if .slnx was created by .NET 9+)
  const slnxPath = join(projectDir, `${projectName}.slnx`);
  if (!dryRun && await fs.pathExists(slnxPath)) {
    // Create classic .sln file manually
    const slnContent = `Microsoft Visual Studio Solution File, Format Version 12.00
# Visual Studio Version 17
VisualStudioVersion = 17.0.31903.59
MinimumVisualStudioVersion = 10.0.40219.1
Global
\tGlobalSection(SolutionConfigurationPlatforms) = preSolution
\t\tDebug|Any CPU = Debug|Any CPU
\t\tRelease|Any CPU = Release|Any CPU
\tEndGlobalSection
EndGlobal
`;
    await fs.writeFile(slnPath, slnContent);
    // Remove .slnx file to avoid confusion
    await fs.remove(slnxPath);
    logger.info('Converted .slnx to .sln for VS Code compatibility');
  }

  // Create projects
  const projects = [
    { proj: `${projectName}.Domain`, type: 'classlib' },
    { proj: `${projectName}.Application`, type: 'classlib' },
    { proj: `${projectName}.Infrastructure`, type: 'classlib' },
    { proj: `${projectName}.Api`, type: 'webapi' },
  ];

  for (const { proj, type } of projects) {
    const projDir = join(srcDir, proj);
    const csprojPath = join(projDir, `${proj}.csproj`);
    if (dryRun || !await fs.pathExists(csprojPath)) {
      execCommand(`dotnet new ${type} -n "${proj}" -o "${projDir}"`, undefined, dryRun);
      execCommand(`dotnet sln "${slnPath}" add "${projDir}"`, undefined, dryRun);
    } else {
      logger.info(chalk.gray(`Project ${proj} already exists, skipping creation`));
    }
  }

  // Create wwwroot directory for static files (required by ASP.NET Core)
  const apiDir = join(srcDir, `${projectName}.Api`);
  if (!dryRun) {
    await fs.ensureDir(join(apiDir, 'wwwroot'));
  }

  // Delete ALL placeholder files created by dotnet new BEFORE any safeWriteFile call.
  // dotnet new webapi/classlib creates default files that safeWriteFile would detect as
  // "modified by user" and skip, preventing SmartStack templates from being written.
  if (!dryRun) {
    // Class1.cs from classlib projects
    for (const { proj, type } of projects) {
      if (type === 'classlib') {
        const class1Path = join(srcDir, proj, 'Class1.cs');
        if (await fs.pathExists(class1Path)) {
          await fs.remove(class1Path);
          logger.info(`Removed placeholder: ${proj}/Class1.cs`);
        }
      }
    }

    // Default files from webapi project (all replaced by SmartStack templates)
    const apiPlaceholders = [
      'Program.cs',
      'appsettings.json',
      'appsettings.Development.json',
      join('Properties', 'launchSettings.json'),
      `${projectName}.Api.http`,
    ];
    for (const placeholder of apiPlaceholders) {
      const placeholderPath = join(apiDir, placeholder);
      if (await fs.pathExists(placeholderPath)) {
        await fs.remove(placeholderPath);
        logger.info(`Removed placeholder: ${projectName}.Api/${placeholder}`);
      }
    }
  }

  // Create launchSettings.json with consistent port 5142
  const launchSettings = {
    $schema: 'https://json.schemastore.org/launchsettings.json',
    profiles: {
      http: {
        commandName: 'Project',
        dotnetRunMessages: true,
        launchBrowser: false,
        applicationUrl: 'http://localhost:5142',
        environmentVariables: {
          ASPNETCORE_ENVIRONMENT: 'Development',
        },
      },
      https: {
        commandName: 'Project',
        dotnetRunMessages: true,
        launchBrowser: false,
        applicationUrl: 'https://localhost:7142;http://localhost:5142',
        environmentVariables: {
          ASPNETCORE_ENVIRONMENT: 'Development',
        },
      },
      Local: {
        commandName: 'Project',
        dotnetRunMessages: true,
        launchBrowser: false,
        // HTTP-ONLY by design (the profile `ss dev` uses): no https URL means
        // UseHttpsRedirection has nothing to 307-redirect to, so the frontend +
        // health probe talk plain HTTP and never hit the untrusted dev cert.
        applicationUrl: 'http://localhost:5142',
        environmentVariables: {
          // Loads appsettings.Local.json (the per-developer / per-branch override).
          ASPNETCORE_ENVIRONMENT: 'Local',
        },
      },
    },
  };
  if (!dryRun) {
    await fs.ensureDir(join(apiDir, 'Properties'));
    const launchRelPath = `src/${projectName}.Api/Properties/launchSettings.json`;
    const launchResult = await safeWriteFile(
      join(apiDir, 'Properties', 'launchSettings.json'),
      JSON.stringify(launchSettings, null, 2),
      findKnownHash(state, 'backend', launchRelPath)
    );
    logSafeWriteResult(launchRelPath, launchResult);
    recordFile(state, 'backend', launchRelPath, launchResult.hash);
  }

  // Remove TargetFramework from .csproj files so they inherit from Directory.Build.props
  if (!dryRun) {
    for (const { proj } of projects) {
      const csprojPath = join(srcDir, proj, `${proj}.csproj`);
      if (await fs.pathExists(csprojPath)) {
        let content = await fs.readFile(csprojPath, 'utf-8');
        // Remove the TargetFramework line (it's inherited from Directory.Build.props)
        content = content.replace(/\s*<TargetFramework>.*<\/TargetFramework>\s*/g, '\n    ');
        await fs.writeFile(csprojPath, content);
      }
    }
    logger.info('Configured projects to inherit TargetFramework from Directory.Build.props');
  }

  // Add project references
  const refs = [
    { from: `${projectName}.Application`, to: `${projectName}.Domain` },
    { from: `${projectName}.Infrastructure`, to: `${projectName}.Domain` },
    { from: `${projectName}.Infrastructure`, to: `${projectName}.Application` },
    { from: `${projectName}.Api`, to: `${projectName}.Application` },
    { from: `${projectName}.Api`, to: `${projectName}.Infrastructure` },
  ];

  for (const { from, to } of refs) {
    execCommand(
      `dotnet add "${join(srcDir, from)}" reference "${join(srcDir, to)}"`,
      undefined,
      dryRun
    );
  }

  // Install NuGet packages (EF Core and essential packages only)
  logger.info('Installing NuGet packages...');

  // Database provider package (always SQL Server)
  const dbPackage = 'Microsoft.EntityFrameworkCore.SqlServer';

  const nugetPackages: { project: string; packages: string[] }[] = [
    {
      project: `${projectName}.Domain`,
      packages: [
        'SmartStack',
      ]
    },
    {
      project: `${projectName}.Infrastructure`,
      packages: [
        'SmartStack',
        'Microsoft.EntityFrameworkCore',
        'Microsoft.EntityFrameworkCore.Design',
        dbPackage,
      ]
    },
    {
      project: `${projectName}.Api`,
      packages: [
        'Microsoft.AspNetCore.OpenApi', // Force stable version before SmartStack
        'SmartStack',
        'Microsoft.AspNetCore.Authentication.JwtBearer',
        'Microsoft.EntityFrameworkCore.Design', // Required for EF Core migrations
        'Swashbuckle.AspNetCore',
        'Microsoft.AspNetCore.SignalR.StackExchangeRedis', // SignalR Redis backplane (not transitive from SmartStack NuGet)
        'Azure.Monitor.OpenTelemetry.AspNetCore', // Observability — replaces legacy Microsoft.ApplicationInsights.AspNetCore
        'Hangfire.AspNetCore', // R6 — Hangfire (not transitive from SmartStack NuGet, PrivateAssets=all)
        'Hangfire.SqlServer', // R6 — Hangfire SQL Server storage
      ]
    },
  ];

  // Use pre-resolved SmartStack version from config (resolved before steps)
  const smartstackVersion = config.smartStackVersion || null;

  for (const { project, packages } of nugetPackages) {
    for (const pkg of packages) {
      // Use explicit version for SmartStack when available
      if (pkg === 'SmartStack' && smartstackVersion) {
        execCommand(`dotnet add "${join(srcDir, project)}" package ${pkg} --version ${smartstackVersion}`, undefined, dryRun);
      } else if (pkg === 'SmartStack' && config.preview) {
        // For SmartStack without explicit version, use --prerelease if preview mode
        execCommand(`dotnet add "${join(srcDir, project)}" package ${pkg} --prerelease`, undefined, dryRun);
      } else {
        // For Microsoft packages, NEVER use --prerelease (avoid incompatibility with current .NET version)
        execCommand(`dotnet add "${join(srcDir, project)}" package ${pkg}`, undefined, dryRun);
      }
    }
  }

  // Override Program.cs with SmartStack + Dual-DbContext integration
  if (!dryRun) {
    const apiDir = join(srcDir, `${projectName}.Api`);

    // Load from template (no fallback - template must exist)
    // CLAUDE.md compliance: CLI deploys templates, never generates code
    const programCs = await loadTemplate('Program.cs.template', projectName);
    const programRelPath = `src/${projectName}.Api/Program.cs`;
    const programResult = await safeWriteFile(
      join(apiDir, 'Program.cs'),
      programCs,
      findKnownHash(state, 'backend', programRelPath)
    );
    logSafeWriteResult(programRelPath, programResult);
    recordFile(state, 'backend', programRelPath, programResult.hash);

    // Create appsettings.json from template
    const templatePath = join(TEMPLATES_DIR, 'appsettings.json.template');

    if (!await fs.pathExists(templatePath)) {
      throw new Error(`Template not found: ${templatePath}`);
    }

    const appSettingsContent = await fs.readFile(templatePath, 'utf-8');
    const appSettings = buildInitialAppSettings(
      appSettingsContent,
      projectName,
      config.email.provider,
    );

    const appSettingsRelPath = `src/${projectName}.Api/appsettings.json`;
    const appSettingsResult = await safeWriteFile(
      join(apiDir, 'appsettings.json'),
      JSON.stringify(appSettings, null, 2),
      findKnownHash(state, 'backend', appSettingsRelPath)
    );
    logSafeWriteResult(appSettingsRelPath, appSettingsResult);
    recordFile(state, 'backend', appSettingsRelPath, appSettingsResult.hash);

    // appsettings.Development.json - Development overrides
    const appSettingsDev = {
      Logging: {
        Sinks: {
          Console: { Enabled: true },
          File: { Enabled: true },
          Seq: { Enabled: false },
          ApplicationInsights: { Enabled: false },
        },
      },
      Email: {
        Provider: config.email.provider,
      },
    };

    const appSettingsDevRelPath = `src/${projectName}.Api/appsettings.Development.json`;
    const appSettingsDevResult = await safeWriteFile(
      join(apiDir, 'appsettings.Development.json'),
      JSON.stringify(appSettingsDev, null, 2),
      findKnownHash(state, 'backend', appSettingsDevRelPath)
    );
    logSafeWriteResult(appSettingsDevRelPath, appSettingsDevResult);
    recordFile(state, 'backend', appSettingsDevRelPath, appSettingsDevResult.hash);
  }

  // Create Dual-DbContext structure from templates
  logger.info('Creating Dual-DbContext structure...');
  await createDualDbContextStructure(projectDir, projectName, state, dryRun);

  // CLAUDE.md - Claude Code project memory (backend layers)
  const backendClaudeMdLayers = [
    { template: 'claude-md/api.CLAUDE.md.template', layer: 'Api' },
    { template: 'claude-md/application.CLAUDE.md.template', layer: 'Application' },
    { template: 'claude-md/domain.CLAUDE.md.template', layer: 'Domain' },
    { template: 'claude-md/infrastructure.CLAUDE.md.template', layer: 'Infrastructure' },
  ];
  for (const { template, layer } of backendClaudeMdLayers) {
    const tplPath = join(TEMPLATES_DIR, template);
    if (await fs.pathExists(tplPath)) {
      const content = await loadTemplate(template, projectName);
      const relPath = `src/${projectName}.${layer}/CLAUDE.md`;
      const result = await safeWriteFile(
        join(projectDir, relPath), content,
        findKnownHash(state, 'backend', relPath)
      );
      logSafeWriteResult(relPath, result);
      recordFile(state, 'backend', relPath, result.hash);
    }
  }
}

async function createConfigFiles(config: ProjectConfig, state: InitState, dryRun: boolean): Promise<void> {
  const { name } = config;

  // Handle absolute paths correctly - use explicit projectDir if provided (in-place mode)
  const projectDir = config.projectDir ?? (isAbsolute(name) ? name : join(process.cwd(), name));
  const projectName = basename(name);

  if (dryRun) {
    logger.info('[DRY RUN] Would create configuration files');
    return;
  }

  // Ensure project directory exists before writing config files
  await fs.ensureDir(projectDir);

  // Directory.Build.props - Always use net10.0
  const targetFramework = 'net10.0';
  const buildProps = `<Project>
  <PropertyGroup>
    <TargetFramework>${targetFramework}</TargetFramework>
    <Nullable>enable</Nullable>
    <ImplicitUsings>enable</ImplicitUsings>
    <TreatWarningsAsErrors>false</TreatWarningsAsErrors>
  </PropertyGroup>
</Project>
`;
  const relPath1 = 'Directory.Build.props';
  const result1 = await safeWriteFile(join(projectDir, relPath1), buildProps, findKnownHash(state, 'config', relPath1));
  logSafeWriteResult(relPath1, result1);
  recordFile(state, 'config', relPath1, result1.hash);
  logger.info(`Target framework: ${chalk.cyan(targetFramework)}${config.preview ? ' (preview)' : ''}`);

  // appsettings.json will be created in createBackendStructure after API project exists

  // .gitignore
  const gitignore = `## .NET
bin/
obj/
*.user
*.suo
.vs/

## EF Core design-time artifacts (Roslyn BuildHost creates corrupted bin folders on Windows)
**/bin?Debug/
**/bin[!a-zA-Z0-9/\\\\]Debug/

## Node
node_modules/
dist/

## IDE
.idea/

## Environment
*.env
*.env.local
*.env.standalone
appsettings.Local.json
appsettings.*.Local.json

## Build
*.log

## Database
*.db
*.db-shm
*.db-wal

## SmartStack BA — per-module re-run baseline (ba-develop compute-page-diff)
.run-snapshot.json
`;
  const relPath2 = '.gitignore';
  const result2 = await safeWriteFile(join(projectDir, relPath2), gitignore, findKnownHash(state, 'config', relPath2));
  logSafeWriteResult(relPath2, result2);
  recordFile(state, 'config', relPath2, result2.hash);

  // .smartstack/config.json - SmartStack project configuration
  // Used by skills/CLIs to detect project structure and DbContext naming
  await fs.ensureDir(join(projectDir, '.smartstack'));
  const smartstackConfig = {
    projectType: 'client',
    dbContext: 'extensions',
    baseNamespace: projectName,
    tablePrefix: '',  // Set later by the BA process (e.g., "rh_", "fi_", "crm_")
    smartStackVersion: config.smartStackVersion || '1.0.0',
    initialized: new Date().toISOString(),
  };
  const relPath3 = '.smartstack/config.json';
  const result3 = await safeWriteFile(
    join(projectDir, relPath3),
    JSON.stringify(smartstackConfig, null, 2),
    findKnownHash(state, 'config', relPath3)
  );
  logSafeWriteResult(relPath3, result3);
  recordFile(state, 'config', relPath3, result3.hash);

  // VS Code configuration
  await fs.ensureDir(join(projectDir, '.vscode'));

  // .vscode/launch.json - Debug configuration
  const launchJson = {
    version: '0.2.0',
    configurations: [
      {
        name: 'Launch API',
        type: 'coreclr',
        request: 'launch',
        preLaunchTask: 'build',
        program: '${workspaceFolder}/src/' + projectName + '.Api/bin/Debug/net10.0/' + projectName + '.Api.dll',
        args: [],
        cwd: '${workspaceFolder}/src/' + projectName + '.Api',
        stopAtEntry: false,
        serverReadyAction: {
          action: 'openExternally',
          pattern: '\\\\bNow listening on:\\\\s+(https?://\\\\S+)',
        },
        env: {
          ASPNETCORE_ENVIRONMENT: 'Development',
          ASPNETCORE_URLS: 'https://localhost:7142;http://localhost:5142',
        },
      },
      {
        name: 'Attach to Process',
        type: 'coreclr',
        request: 'attach',
      },
    ],
  };
  const relPath4 = '.vscode/launch.json';
  const result4 = await safeWriteFile(join(projectDir, relPath4), JSON.stringify(launchJson, null, 2), findKnownHash(state, 'config', relPath4));
  logSafeWriteResult(relPath4, result4);
  recordFile(state, 'config', relPath4, result4.hash);

  // .vscode/tasks.json - Build and run tasks
  const tasksJson = {
    version: '2.0.0',
    tasks: [
      {
        label: 'build',
        command: 'dotnet',
        type: 'process',
        args: ['build', '${workspaceFolder}/' + projectName + '.sln', '/property:GenerateFullPaths=true', '/consoleloggerparameters:NoSummary'],
        problemMatcher: '$msCompile',
        group: { kind: 'build', isDefault: true },
      },
      {
        label: 'watch',
        command: 'dotnet',
        type: 'process',
        args: ['watch', 'run', '--project', '${workspaceFolder}/src/' + projectName + '.Api'],
        problemMatcher: '$msCompile',
        isBackground: true,
      },
      {
        label: 'test',
        command: 'dotnet',
        type: 'process',
        args: ['test', '${workspaceFolder}/' + projectName + '.sln'],
        problemMatcher: '$msCompile',
        group: { kind: 'test', isDefault: true },
      },
      {
        label: 'ef-migrations-add',
        command: 'dotnet',
        type: 'process',
        args: ['ef', 'migrations', 'add', '${input:migrationName}', '-p', 'src/' + projectName + '.Infrastructure', '-s', 'src/' + projectName + '.Api'],
        problemMatcher: '$msCompile',
      },
      {
        label: 'ef-database-update',
        command: 'dotnet',
        type: 'process',
        args: ['ef', 'database', 'update', '-p', 'src/' + projectName + '.Infrastructure', '-s', 'src/' + projectName + '.Api'],
        problemMatcher: '$msCompile',
      },
    ],
    inputs: [
      {
        id: 'migrationName',
        description: 'Name of the migration',
        default: 'NewMigration',
        type: 'promptString',
      },
    ],
  };
  const relPath5 = '.vscode/tasks.json';
  const result5 = await safeWriteFile(join(projectDir, relPath5), JSON.stringify(tasksJson, null, 2), findKnownHash(state, 'config', relPath5));
  logSafeWriteResult(relPath5, result5);
  recordFile(state, 'config', relPath5, result5.hash);

  // .vscode/extensions.json - Recommended extensions
  const extensionsJson = {
    recommendations: [
      'ms-dotnettools.csdevkit',
      'ms-dotnettools.csharp',
      'humao.rest-client',
      'ms-azuretools.vscode-docker',
      'EditorConfig.EditorConfig',
      'eamodio.gitlens',
      'dbaeumer.vscode-eslint',
      'bradlc.vscode-tailwindcss',
      'esbenp.prettier-vscode',
    ],
  };
  const relPath6 = '.vscode/extensions.json';
  const result6 = await safeWriteFile(join(projectDir, relPath6), JSON.stringify(extensionsJson, null, 2), findKnownHash(state, 'config', relPath6));
  logSafeWriteResult(relPath6, result6);
  recordFile(state, 'config', relPath6, result6.hash);

  // README.md
  const readme = `# ${projectName}

A SmartStack application with .NET backend and React frontend.

## Prerequisites

- .NET 10.0 SDK
- Node.js 20+ and npm

## Getting Started

### Backend

\`\`\`bash
# Restore packages
dotnet restore

# Create initial migration
dotnet ef migrations add InitialCreate -p src/${projectName}.Infrastructure -s src/${projectName}.Api

# Apply migrations
dotnet ef database update -p src/${projectName}.Infrastructure -s src/${projectName}.Api

# Run the API
dotnet run --project src/${projectName}.Api
\`\`\`

### Frontend

\`\`\`bash
# Navigate to frontend directory
cd web/${projectName.toLowerCase()}-web

# Install dependencies
npm install

# Start development server
npm run dev
\`\`\`

The frontend runs on http://localhost:3000 and proxies API calls to http://localhost:5142.

## Project Structure

\`\`\`
${projectName}/
├── src/
│   ├── ${projectName}.Domain/          # Domain entities and interfaces
│   ├── ${projectName}.Application/     # Application services and DTOs
│   ├── ${projectName}.Infrastructure/  # EF Core, repositories, external services
│   └── ${projectName}.Api/             # Web API controllers and configuration
├── web/
│   └── ${projectName.toLowerCase()}-web/              # React frontend (Vite + TypeScript + Tailwind)
├── docker-images/                       # Dockerfiles + docker-compose (backend + frontend)
└── tests/                               # Unit and integration tests
\`\`\`

## Development

1. Start the backend: \`dotnet run --project src/${projectName}.Api\`
2. Start the frontend: \`cd web/${projectName.toLowerCase()}-web && npm run dev\`
3. Open http://localhost:3000

## Documentation

- SmartStack: https://docs.smartstack.app
- API Reference: http://localhost:5142/scalar (when running)

---

Generated with [SmartStack CLI](https://atlashub.io/products/smartstack-cli)
`;
  const relPath7 = 'README.md';
  const result7 = await safeWriteFile(join(projectDir, relPath7), readme, findKnownHash(state, 'config', relPath7));
  logSafeWriteResult(relPath7, result7);
  recordFile(state, 'config', relPath7, result7.hash);

  // .claude/settings.json - Claude Code project settings (env vars, agent teams)
  const claudeSettingsTemplatePath = join(TEMPLATES_DIR, 'claude-settings.json.template');
  if (await fs.pathExists(claudeSettingsTemplatePath)) {
    await fs.ensureDir(join(projectDir, '.claude'));
    const claudeSettingsContent = await fs.readFile(claudeSettingsTemplatePath, 'utf-8');
    const relPath8 = '.claude/settings.json';
    const result8 = await safeWriteFile(
      join(projectDir, relPath8),
      claudeSettingsContent,
      findKnownHash(state, 'config', relPath8)
    );
    logSafeWriteResult(relPath8, result8);
    recordFile(state, 'config', relPath8, result8.hash);
  }

  // CLAUDE.md - Claude Code project memory (root)
  const claudeMdTemplatePath = join(TEMPLATES_DIR, 'claude-md', 'root.CLAUDE.md.template');
  if (await fs.pathExists(claudeMdTemplatePath)) {
    const claudeMdContent = await loadTemplate('claude-md/root.CLAUDE.md.template', projectName);
    const claudeMd_relPath = 'CLAUDE.md';
    const claudeMd_result = await safeWriteFile(
      join(projectDir, claudeMd_relPath), claudeMdContent,
      findKnownHash(state, 'config', claudeMd_relPath)
    );
    logSafeWriteResult(claudeMd_relPath, claudeMd_result);
    recordFile(state, 'config', claudeMd_relPath, claudeMd_result.hash);
  }
}

async function createFrontendStructure(config: ProjectConfig, state: InitState, dryRun: boolean): Promise<void> {
  const { name } = config;

  // Handle absolute paths correctly - use explicit projectDir if provided (in-place mode)
  const projectDir = config.projectDir ?? (isAbsolute(name) ? name : join(process.cwd(), name));
  const projectName = basename(name);
  const webDir = join(projectDir, 'web', `${projectName.toLowerCase()}-web`);
  const webRelPrefix = `web/${projectName.toLowerCase()}-web`;

  logger.info('Creating React frontend structure...');

  if (dryRun) {
    logger.info('[DRY RUN] Would create React frontend at: ' + webDir);
    return;
  }

  // Create web directory
  await fs.ensureDir(webDir);
  await fs.ensureDir(join(webDir, 'src'));
  await fs.ensureDir(join(webDir, 'src', 'components'));
  await fs.ensureDir(join(webDir, 'src', 'pages'));
  await fs.ensureDir(join(webDir, 'src', 'services'));
  await fs.ensureDir(join(webDir, 'src', 'hooks'));
  await fs.ensureDir(join(webDir, 'src', 'types'));
  await fs.ensureDir(join(webDir, 'src', 'i18n'));
  await fs.ensureDir(join(webDir, 'src', 'extensions'));
  await fs.ensureDir(join(webDir, 'public'));

  // package.json
  const packageJson = {
    name: `${projectName.toLowerCase()}-web`,
    private: true,
    version: '0.0.0',
    type: 'module',
    scripts: {
      // Patches @atlashub/smartstack/theme.css for Tailwind v4 parser (see scripts/patch-smartstack-theme.cjs).
      postinstall: 'node scripts/patch-smartstack-theme.cjs',
      dev: 'vite',
      build: 'tsc -b && vite build',
      'build:prod': 'vite build',
      typecheck: 'tsc -b',
      lint: 'eslint .',
      preview: 'vite preview',
      test: 'vitest run',
      'test:watch': 'vitest',
      'test:coverage': 'vitest run --coverage',
    },
    dependencies: {
      '@atlashub/smartstack': `^${config.smartStackVersion || '3.0.0'}`,
      '@microsoft/signalr': '^10.0.0',
      '@tailwindcss/vite': '^4.1.18',
      axios: '^1.13.2',
      clsx: '^2.1.0',
      i18next: '^25.7.3',
      'i18next-browser-languagedetector': '^8.2.0',
      'lucide-react': '^0.562.0',
      react: '^19.0.0',
      'react-dom': '^19.0.0',
      'react-error-boundary': '^6.0.2',
      'react-i18next': '^16.5.1',
      'react-router-dom': '^7.0.0',
      tailwindcss: '^4.1.18',
    },
    devDependencies: {
      '@eslint/js': '^9.39.1',
      '@testing-library/jest-dom': '^6.9.1',
      '@testing-library/react': '^16.3.2',
      '@testing-library/user-event': '^14.6.1',
      '@types/node': '^24.10.1',
      '@types/react': '^19.2.5',
      '@types/react-dom': '^19.2.3',
      '@vitejs/plugin-react': '^5.1.1',
      '@vitest/coverage-v8': '^4.0.18',
      eslint: '^9.39.1',
      'eslint-plugin-react-hooks': '^7.0.1',
      'eslint-plugin-react-refresh': '^0.4.24',
      globals: '^16.5.0',
      jsdom: '^28.0.0',
      msw: '^2.7.5',
      typescript: '~5.9.3',
      'typescript-eslint': '^8.46.4',
      vite: '^7.2.4',
      vitest: '^4.0.18',
    },
  };
  const pkg_relPath = `${webRelPrefix}/package.json`;
  const pkg_result = await safeWriteFile(join(webDir, 'package.json'), JSON.stringify(packageJson, null, 2), findKnownHash(state, 'frontend', pkg_relPath));
  logSafeWriteResult(pkg_relPath, pkg_result);
  recordFile(state, 'frontend', pkg_relPath, pkg_result.hash);

  // Postinstall script: patches @atlashub/smartstack/theme.css to neutralize an
  // upstream comment that breaks Tailwind v4's CSS parser. Runs automatically on
  // every `npm install` via the `postinstall` script wired in package.json above.
  const patchScript = await loadTemplate('patch-smartstack-theme.cjs.template', projectName);
  await fs.ensureDir(join(webDir, 'scripts'));
  const patchRelPath = `${webRelPrefix}/scripts/patch-smartstack-theme.cjs`;
  const patchResult = await safeWriteFile(
    join(webDir, 'scripts', 'patch-smartstack-theme.cjs'),
    patchScript,
    findKnownHash(state, 'frontend', patchRelPath),
  );
  logSafeWriteResult(patchRelPath, patchResult);
  recordFile(state, 'frontend', patchRelPath, patchResult.hash);

  // .env - Environment configuration (gitignored, local config)
  const envFile = `# =============================================================================
# ${projectName} - Frontend Environment Configuration
# =============================================================================

# Application name (displayed in UI)
VITE_APP_NAME=${projectName}

# Backend API URL (used by Vite proxy in development)
VITE_API_URL=http://localhost:5142

# WebSocket URL for SignalR (defaults to VITE_API_URL if not set)
# VITE_WS_URL=http://localhost:5142

# Frontend development server port
VITE_PORT=3000

# Public URL (for production deployment, used in OAuth callbacks)
# VITE_PUBLIC_URL=https://myapp.example.com

# Feature flags
# VITE_ENABLE_DEVTOOLS=true
`;
  const env_relPath = `${webRelPrefix}/.env`;
  const env_result = await safeWriteFile(join(webDir, '.env'), envFile, findKnownHash(state, 'frontend', env_relPath));
  logSafeWriteResult(env_relPath, env_result);
  recordFile(state, 'frontend', env_relPath, env_result.hash);

  // .env.example - Template for environment configuration (committed to git)
  const envExampleFile = `# =============================================================================
# ${projectName} - Frontend Environment Configuration
# =============================================================================
# Copy this file to .env and customize for your environment
# .env is gitignored and should contain your local/production settings

# Application name (displayed in UI)
VITE_APP_NAME=${projectName}

# Backend API URL (used by Vite proxy in development)
# In production, configure your reverse proxy (nginx, etc.) to route /api to backend
VITE_API_URL=http://localhost:5142

# WebSocket URL for SignalR (defaults to VITE_API_URL if not set)
# Useful when WebSocket server runs on different host/port
# VITE_WS_URL=http://localhost:5142

# Frontend development server port
VITE_PORT=3000

# Public URL (for production deployment)
# Used for OAuth callbacks and absolute URLs in emails
# VITE_PUBLIC_URL=https://myapp.example.com

# Feature flags (optional)
# VITE_ENABLE_DEVTOOLS=true
`;
  const envEx_relPath = `${webRelPrefix}/.env.example`;
  const envEx_result = await safeWriteFile(join(webDir, '.env.example'), envExampleFile, findKnownHash(state, 'frontend', envEx_relPath));
  logSafeWriteResult(envEx_relPath, envEx_result);
  recordFile(state, 'frontend', envEx_relPath, envEx_result.hash);

  // vite.config.ts with proxy to backend (reads from .env)
  const viteConfig = `import { defineConfig, loadEnv, type Plugin } from 'vite';
import { readFile } from 'node:fs/promises';
import react from '@vitejs/plugin-react';
import tailwindcss from '@tailwindcss/vite';

// Upstream workaround: @atlashub/smartstack's theme.css contains a comment
// with the pattern \`**/*.js\` whose \`*/\` closes the comment early and breaks
// Tailwind v4's CSS parser ("Unterminated string: ';'"). Rewrite on load.
function fixSmartstackThemeComment(): Plugin {
  return {
    name: 'fix-smartstack-theme-comment',
    enforce: 'pre',
    async load(id) {
      const normalized = id.split('?')[0].replace(/\\\\/g, '/');
      if (!normalized.endsWith('@atlashub/smartstack/dist/theme.css')) return null;
      const content = await readFile(normalized, 'utf-8');
      return content.replace(/\\*\\*\\/\\*\\.js/g, '** /*.js');
    },
  };
}

export default defineConfig(({ mode }) => {
  const env = loadEnv(mode, process.cwd(), '');

  // Configuration from environment
  const apiUrl = env.VITE_API_URL || 'http://localhost:5142';
  const wsUrl = env.VITE_WS_URL || apiUrl; // WebSocket URL defaults to API URL
  const port = parseInt(env.VITE_PORT || '3000', 10);

  return {
    plugins: [react(), fixSmartstackThemeComment(), tailwindcss()],
    server: {
      port,
      proxy: {
        // API proxy - REST endpoints
        '/api': {
          target: apiUrl,
          changeOrigin: true,
          secure: false,
          bypass(req, _res, _options) {
            // Prevent SPA routes like /api/accounts (React components) from being proxied
            if (req.headers.accept?.includes('text/html') &&
                !req.url?.startsWith('/api/auth/')) {
              return req.url;
            }
          },
        },
        // SignalR WebSocket proxy
        '/hubs': {
          target: wsUrl,
          changeOrigin: true,
          secure: false,
          ws: true,
        },
      },
    },
    // Path alias for @/ imports (matches tsconfig.json paths)
    resolve: {
      alias: {
        '@': '/src',
      },
    },
    // Make env variables available to the app
    define: {
      __APP_NAME__: JSON.stringify(env.VITE_APP_NAME || '${projectName}'),
    },
  };
});
`;
  const vite_relPath = `${webRelPrefix}/vite.config.ts`;
  const vite_result = await safeWriteFile(join(webDir, 'vite.config.ts'), viteConfig, findKnownHash(state, 'frontend', vite_relPath));
  logSafeWriteResult(vite_relPath, vite_result);
  recordFile(state, 'frontend', vite_relPath, vite_result.hash);

  // tsconfig.json
  const tsConfig = {
    compilerOptions: {
      target: 'ES2022',
      useDefineForClassFields: true,
      lib: ['ES2022', 'DOM', 'DOM.Iterable'],
      module: 'ESNext',
      skipLibCheck: true,
      moduleResolution: 'bundler',
      allowImportingTsExtensions: true,
      verbatimModuleSyntax: true,
      erasableSyntaxOnly: true,
      moduleDetection: 'force',
      noEmit: true,
      jsx: 'react-jsx',
      strict: true,
      noUnusedLocals: true,
      noUnusedParameters: true,
      noFallthroughCasesInSwitch: true,
      noUncheckedSideEffectImports: true,
      baseUrl: '.',
      paths: {
        '@/*': ['src/*'],
      },
    },
    include: ['src'],
  };
  const tsc_relPath = `${webRelPrefix}/tsconfig.json`;
  const tsc_result = await safeWriteFile(join(webDir, 'tsconfig.json'), JSON.stringify(tsConfig, null, 2), findKnownHash(state, 'frontend', tsc_relPath));
  logSafeWriteResult(tsc_relPath, tsc_result);
  recordFile(state, 'frontend', tsc_relPath, tsc_result.hash);

  // index.html
  const indexHtml = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <link rel="icon" type="image/svg+xml" href="/vite.svg" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${projectName}</title>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.tsx"></script>
  </body>
</html>
`;
  const html_relPath = `${webRelPrefix}/index.html`;
  const html_result = await safeWriteFile(join(webDir, 'index.html'), indexHtml, findKnownHash(state, 'frontend', html_relPath));
  logSafeWriteResult(html_relPath, html_result);
  recordFile(state, 'frontend', html_relPath, html_result.hash);

  // src/main.tsx
  const mainTsx = `import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { SmartStackProvider } from '@atlashub/smartstack';
import './extensions/componentRegistry.generated';
import App from './App';
import './index.css';
import './i18n';

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <SmartStackProvider
        config={{
          apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:5142',
          extensions: {},
          // Mobile shell: narrow viewports render the package's hierarchical
          // "descente par paliers" shell (Applications -> Modules -> Sections)
          // with its transverse bottom bar. It is ON BY DEFAULT — this block
          // only makes the choice explicit and carries the tunables
          // (breakpoint, shellComponent, bottomNav). The /pwa skill rewrites it.
          mobile: { enabled: true }
        }}
      >
        <App />
      </SmartStackProvider>
    </BrowserRouter>
  </StrictMode>,
);
`;
  const main_relPath = `${webRelPrefix}/src/main.tsx`;
  const main_result = await safeWriteFile(join(webDir, 'src', 'main.tsx'), mainTsx, findKnownHash(state, 'frontend', main_relPath));
  logSafeWriteResult(main_relPath, main_result);
  recordFile(state, 'frontend', main_relPath, main_result.hash);

  // src/App.tsx - Uses DynamicRouter (v3.7+) for API-driven routing
  const appTsx = `import { DynamicRouter } from '@atlashub/smartstack';

/**
 * App - Main application component
 *
 * DynamicRouter generates routes from the /api/navigation/menu response.
 * Pages are resolved via PageRegistry — register them in componentRegistry.generated.ts.
 *
 * To add custom pages:
 * 1. Create your page component in src/pages/
 * 2. Register it with PageRegistry.register('your.page.key', lazy(() => import('./pages/YourPage')))
 * 3. Add navigation seed data with matching ComponentKey in the backend
 * 4. DynamicRouter resolves it automatically — no manual route wiring needed
 */
function App() {
  return <DynamicRouter />;
}

export default App;
`;
  const app_relPath = `${webRelPrefix}/src/App.tsx`;
  const app_result = await safeWriteFile(join(webDir, 'src', 'App.tsx'), appTsx, findKnownHash(state, 'frontend', app_relPath));
  logSafeWriteResult(app_relPath, app_result);
  recordFile(state, 'frontend', app_relPath, app_result.hash);

  // src/extensions/componentRegistry.generated.ts — stub for DynamicRouter
  // Clients run `scaffold_routes` to populate with their PageRegistry.register() calls
  const componentRegistryTs = `/**
 * Component Registry
 *
 * Auto-generated by SmartStack scaffold-routes skill — DO NOT EDIT MANUALLY
 * Re-run \`/scaffold-routes\` to regenerate with your project's pages.
 */
import { PageRegistry } from '@atlashub/smartstack';

// Register your pages here (auto-generated by scaffold-routes):
// PageRegistry.register('your.page.key', lazy(() => import('./pages/YourPage').then(m => ({ default: m.YourPage }))));
`;
  const registry_relPath = `${webRelPrefix}/src/extensions/componentRegistry.generated.ts`;
  const registry_result = await safeWriteFile(join(webDir, 'src', 'extensions', 'componentRegistry.generated.ts'), componentRegistryTs, findKnownHash(state, 'frontend', registry_relPath));
  logSafeWriteResult(registry_relPath, registry_result);
  recordFile(state, 'frontend', registry_relPath, registry_result.hash);

  logger.info(`Stub componentRegistry created. Run ${chalk.cyan('/scaffold-routes')} in Claude Code to register all built-in + custom pages — required to avoid MISSING_PAGE errors at runtime.`);

  // src/index.css with Tailwind v4
  // - @import "@atlashub/smartstack/theme.css" loads design tokens (--color-primary-*, --color-accent-*, font Inter) and @custom-variant dark
  // - @source ensures Tailwind scans SmartStack package bundle for responsive classes (sm:flex, lg:hidden, etc.)
  // - Order matters: all @import directives must precede @source per CSS spec
  const indexCss = `@import "tailwindcss";
@import "@atlashub/smartstack/theme.css";
@source "../node_modules/@atlashub/smartstack/dist/**/*.js";
`;
  const css_relPath = `${webRelPrefix}/src/index.css`;
  const css_result = await safeWriteFile(join(webDir, 'src', 'index.css'), indexCss, findKnownHash(state, 'frontend', css_relPath));
  logSafeWriteResult(css_relPath, css_result);
  recordFile(state, 'frontend', css_relPath, css_result.hash);

  // Note: HomePage.tsx is no longer needed - SmartStack routes provide all pages

  // src/i18n/index.ts
  // Do NOT create a parallel i18next instance here: the @atlashub/smartstack SDK
  // boots its OWN instance and its init() REPLACES the resource store, so a
  // separate instance loaded before it is silently clobbered (pages then render
  // raw i18n keys). The supported seam is addClientResources(), which writes into
  // the SDK's live instance. Business-module namespaces are registered
  // automatically by src/extensions/moduleResources.generated.ts (emitted by
  // aggregate-component-registry, imported AFTER the SDK init). This file is only
  // for app-wide, hand-added client resources. Content is the shared
  // I18N_NOOP_TEMPLATE — the i18n-neutralize-parallel-init migration writes the
  // exact same bytes, so a migrated file hashes identical on the next init.
  const i18nConfig = I18N_NOOP_TEMPLATE;
  const i18n_relPath = `${webRelPrefix}/src/i18n/index.ts`;
  const i18n_result = await safeWriteFile(join(webDir, 'src', 'i18n', 'index.ts'), i18nConfig, findKnownHash(state, 'frontend', i18n_relPath));
  logSafeWriteResult(i18n_relPath, i18n_result);
  recordFile(state, 'frontend', i18n_relPath, i18n_result.hash);

  // src/services/api.ts - Load from template (CLAUDE.md compliance)
  const apiService = await loadTemplate('api.ts.template', projectName);
  const api_relPath = `${webRelPrefix}/src/services/api.ts`;
  const api_result = await safeWriteFile(join(webDir, 'src', 'services', 'api.ts'), apiService, findKnownHash(state, 'frontend', api_relPath));
  logSafeWriteResult(api_relPath, api_result);
  recordFile(state, 'frontend', api_relPath, api_result.hash);

  // src/vite-env.d.ts
  const viteEnvDts = `/// <reference types="vite/client" />
`;
  const envDts_relPath = `${webRelPrefix}/src/vite-env.d.ts`;
  const envDts_result = await safeWriteFile(join(webDir, 'src', 'vite-env.d.ts'), viteEnvDts, findKnownHash(state, 'frontend', envDts_relPath));
  logSafeWriteResult(envDts_relPath, envDts_result);
  recordFile(state, 'frontend', envDts_relPath, envDts_result.hash);

  // public/vite.svg
  const viteSvg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" role="img" class="iconify iconify--logos" width="31.88" height="32" preserveAspectRatio="xMidYMid meet" viewBox="0 0 256 257"><defs><linearGradient id="IconifyId1813088fe1fbc01fb466" x1="-.828%" x2="57.636%" y1="7.652%" y2="78.411%"><stop offset="0%" stop-color="#41D1FF"></stop><stop offset="100%" stop-color="#BD34FE"></stop></linearGradient><linearGradient id="IconifyId1813088fe1fbc01fb467" x1="43.376%" x2="50.316%" y1="2.242%" y2="89.03%"><stop offset="0%" stop-color="#FFBD4F"></stop><stop offset="100%" stop-color="#FF980E"></stop></linearGradient></defs><path fill="url(#IconifyId1813088fe1fbc01fb466)" d="M255.153 37.938L134.897 252.976c-2.483 4.44-8.862 4.466-11.382.048L.875 37.958c-2.746-4.814 1.371-10.646 6.827-9.67l120.385 21.517a6.537 6.537 0 0 0 2.322-.004l117.867-21.483c5.438-.991 9.574 4.796 6.877 9.62Z"></path><path fill="url(#IconifyId1813088fe1fbc01fb467)" d="M185.432.063L96.44 17.501a3.268 3.268 0 0 0-2.634 3.014l-5.474 92.456a3.268 3.268 0 0 0 3.997 3.378l24.777-5.718c2.318-.535 4.413 1.507 3.936 3.838l-7.361 36.047c-.495 2.426 1.782 4.5 4.151 3.78l15.304-4.649c2.372-.72 4.652 1.36 4.15 3.788l-11.698 56.621c-.732 3.542 3.979 5.473 5.943 2.437l1.313-2.028l72.516-144.72c1.215-2.423-.88-5.186-3.54-4.672l-25.505 4.922c-2.396.462-4.435-1.77-3.759-4.114l16.646-57.705c.677-2.35-1.37-4.583-3.769-4.113Z"></path></svg>
`;
  const svg_relPath = `${webRelPrefix}/public/vite.svg`;
  const svg_result = await safeWriteFile(join(webDir, 'public', 'vite.svg'), viteSvg, findKnownHash(state, 'frontend', svg_relPath));
  logSafeWriteResult(svg_relPath, svg_result);
  recordFile(state, 'frontend', svg_relPath, svg_result.hash);

  // .gitignore for web
  const webGitignore = `# Dependencies
node_modules/

# Build
dist/
build/

# IDE
.idea/
.vscode/

# Environment
.env
.env.local
.env.standalone
.env.*.local
.env.*.standalone

# Logs
*.log
npm-debug.log*

# OS
.DS_Store
Thumbs.db
`;
  const gi_relPath = `${webRelPrefix}/.gitignore`;
  const gi_result = await safeWriteFile(join(webDir, '.gitignore'), webGitignore, findKnownHash(state, 'frontend', gi_relPath));
  logSafeWriteResult(gi_relPath, gi_result);
  recordFile(state, 'frontend', gi_relPath, gi_result.hash);

  // CLAUDE.md - Claude Code project memory (web)
  const webClaudeMdTplPath = join(TEMPLATES_DIR, 'claude-md', 'web.CLAUDE.md.template');
  if (await fs.pathExists(webClaudeMdTplPath)) {
    const webClaudeMdContent = await loadTemplate('claude-md/web.CLAUDE.md.template', projectName);
    const webClaudeMd_relPath = `${webRelPrefix}/CLAUDE.md`;
    const webClaudeMd_result = await safeWriteFile(
      join(webDir, 'CLAUDE.md'), webClaudeMdContent,
      findKnownHash(state, 'frontend', webClaudeMd_relPath)
    );
    logSafeWriteResult(webClaudeMd_relPath, webClaudeMd_result);
    recordFile(state, 'frontend', webClaudeMd_relPath, webClaudeMd_result.hash);
  }

  // Test infrastructure (vitest + MSW)
  const testFrontendDir = join(TEMPLATES_DIR, 'test-frontend');
  if (await fs.pathExists(testFrontendDir)) {
    await fs.ensureDir(join(webDir, 'src', 'test', 'msw'));

    // vitest.config.ts → web root
    const vitestContent = await fs.readFile(join(testFrontendDir, 'vitest.config.ts'), 'utf-8');
    const vitest_relPath = `${webRelPrefix}/vitest.config.ts`;
    const vitest_result = await safeWriteFile(
      join(webDir, 'vitest.config.ts'), vitestContent,
      findKnownHash(state, 'frontend', vitest_relPath)
    );
    logSafeWriteResult(vitest_relPath, vitest_result);
    recordFile(state, 'frontend', vitest_relPath, vitest_result.hash);

    // Test setup and utilities → src/test/
    const testFiles: Array<{ src: string; dest: string }> = [
      { src: 'setup.ts', dest: 'src/test/setup.ts' },
      { src: 'test-utils.tsx', dest: 'src/test/test-utils.tsx' },
      { src: 'msw/handlers.ts', dest: 'src/test/msw/handlers.ts' },
      { src: 'msw/server.ts', dest: 'src/test/msw/server.ts' },
    ];
    for (const { src, dest } of testFiles) {
      const content = await fs.readFile(join(testFrontendDir, src), 'utf-8');
      const relPath = `${webRelPrefix}/${dest}`;
      const result = await safeWriteFile(
        join(webDir, dest), content,
        findKnownHash(state, 'frontend', relPath)
      );
      logSafeWriteResult(relPath, result);
      recordFile(state, 'frontend', relPath, result.hash);
    }
  }

  logger.success('React frontend created at: ' + webDir);

  // Install npm dependencies so the project is immediately runnable.
  // Soft-fail: if npm is missing or install errors, log a warning and continue —
  // the user can run `npm install` manually later.
  if (!dryRun) {
    logger.info('Installing frontend dependencies (npm install)...');
    try {
      execSync('npm install', { cwd: webDir, stdio: 'inherit' });
    } catch {
      logger.warning(
        `npm install failed in ${webDir}. Run it manually: cd ${webDir} && npm install`
      );
    }
  } else {
    logger.info('[DRY RUN] Would run: npm install');
  }
}

async function createDockerFiles(config: ProjectConfig, state: InitState, dryRun: boolean): Promise<void> {
  const { name } = config;
  const projectDir = config.projectDir ?? (isAbsolute(name) ? name : join(process.cwd(), name));
  const projectName = basename(name);

  // Ensure docker-images/ directory exists
  if (!dryRun) {
    await fs.ensureDir(join(projectDir, 'docker-images'));
  }

  // Backend Dockerfile
  const backendDockerfile = await loadTemplate('Dockerfile.backend.template', projectName);
  const backendRelPath = 'docker-images/Dockerfile.backend';
  if (dryRun) {
    logger.info(`Would create ${backendRelPath}`);
  } else {
    const backendResult = await safeWriteFile(
      join(projectDir, backendRelPath), backendDockerfile,
      findKnownHash(state, 'docker', backendRelPath)
    );
    logSafeWriteResult(backendRelPath, backendResult);
    recordFile(state, 'docker', backendRelPath, backendResult.hash);
  }

  // Frontend Dockerfile
  const frontendDockerfile = await loadTemplate('Dockerfile.frontend.template', projectName);
  const frontendRelPath = 'docker-images/Dockerfile.frontend';
  if (dryRun) {
    logger.info(`Would create ${frontendRelPath}`);
  } else {
    const frontendResult = await safeWriteFile(
      join(projectDir, frontendRelPath), frontendDockerfile,
      findKnownHash(state, 'docker', frontendRelPath)
    );
    logSafeWriteResult(frontendRelPath, frontendResult);
    recordFile(state, 'docker', frontendRelPath, frontendResult.hash);
  }

  // Frontend Nginx config (envsubst template: ${API_FQDN} injected at runtime).
  // Copied by Dockerfile.frontend to /etc/nginx/templates/default.conf.template.
  const frontendNginx = await loadTemplate('nginx-frontend.conf.template', projectName);
  const frontendNginxRelPath = 'docker-images/nginx-frontend.conf';
  if (dryRun) {
    logger.info(`Would create ${frontendNginxRelPath}`);
  } else {
    const frontendNginxResult = await safeWriteFile(
      join(projectDir, frontendNginxRelPath), frontendNginx,
      findKnownHash(state, 'docker', frontendNginxRelPath)
    );
    logSafeWriteResult(frontendNginxRelPath, frontendNginxResult);
    recordFile(state, 'docker', frontendNginxRelPath, frontendNginxResult.hash);
  }

  // .dockerignore
  const dockerignore = await loadTemplate('dockerignore.template', projectName);
  const dockerignoreRelPath = '.dockerignore';
  if (dryRun) {
    logger.info(`Would create ${dockerignoreRelPath}`);
  } else {
    const dockerignoreResult = await safeWriteFile(
      join(projectDir, dockerignoreRelPath), dockerignore,
      findKnownHash(state, 'docker', dockerignoreRelPath)
    );
    logSafeWriteResult(dockerignoreRelPath, dockerignoreResult);
    recordFile(state, 'docker', dockerignoreRelPath, dockerignoreResult.hash);
  }

  // Docker Compose
  const dockerCompose = await loadTemplate('docker-compose.yml.template', projectName);
  const composeRelPath = 'docker-images/docker-compose.yml';
  if (dryRun) {
    logger.info(`Would create ${composeRelPath}`);
  } else {
    const composeResult = await safeWriteFile(
      join(projectDir, composeRelPath), dockerCompose,
      findKnownHash(state, 'docker', composeRelPath)
    );
    logSafeWriteResult(composeRelPath, composeResult);
    recordFile(state, 'docker', composeRelPath, composeResult.hash);
  }

  logger.success('Docker configuration created');
}

function checkGitIdentity(cwd?: string): { hasName: boolean; hasEmail: boolean } {
  const check = (key: string): boolean => {
    try {
      const result = spawnSync('git', ['config', key], { encoding: 'utf-8', shell: true, cwd, timeout: 5000 });
      return result.status === 0 && !!result.stdout.trim();
    } catch {
      return false;
    }
  };
  return { hasName: check('user.name'), hasEmail: check('user.email') };
}

async function ensureGitIdentity(projectDir: string, dryRun: boolean): Promise<void> {
  if (dryRun) return;

  const identity = checkGitIdentity(projectDir);
  if (identity.hasName && identity.hasEmail) return;

  logger.warning('Git user identity is not configured.');

  const answers = await inquirer.prompt([
    {
      type: 'input',
      name: 'gitName',
      message: 'Git user name:',
      validate: (v: string) => v.trim().length > 0 || 'Name is required',
      when: () => !identity.hasName,
    },
    {
      type: 'input',
      name: 'gitEmail',
      message: 'Git user email:',
      validate: (v: string) => v.trim().length > 0 || 'Email is required',
      when: () => !identity.hasEmail,
    },
    {
      type: 'list',
      name: 'scope',
      message: 'Set this identity for:',
      choices: [
        { name: 'This repository only (--local)', value: 'local' },
        { name: 'All repositories (--global)', value: 'global' },
      ],
      default: 'global',
    },
  ]);

  const flag = answers.scope === 'global' ? '--global' : '--local';
  if (answers.gitName) {
    execCommand(`git config ${flag} user.name "${answers.gitName}"`, projectDir);
  }
  if (answers.gitEmail) {
    execCommand(`git config ${flag} user.email "${answers.gitEmail}"`, projectDir);
  }
  logger.success('Git identity configured');
}

async function initializeGit(config: ProjectConfig, dryRun: boolean): Promise<void> {
  const { name } = config;

  // Handle absolute paths correctly - use explicit projectDir if provided (in-place mode)
  const projectDir = config.projectDir ?? (isAbsolute(name) ? name : join(process.cwd(), name));

  // Check if git is already initialized (e.g., GitFlow worktree)
  const gitDir = join(projectDir, '.git');
  const isGitInitialized = await fs.pathExists(gitDir);

  if (isGitInitialized) {
    // Git already initialized (e.g., via /gitflow init) — skip git operations entirely.
    // User will commit scaffolded files via their own workflow (e.g., /gitflow:3-commit).
    logger.info('Git repository already initialized, skipping git setup (commit via your git workflow).');
    return;
  }

  // No git repo exists — bootstrap a fresh one with initial commit
  logger.info('Initializing Git repository...');
  execCommand('git init -b main', projectDir, dryRun);

  await ensureGitIdentity(projectDir, dryRun);

  execCommand('git add .', projectDir, dryRun);

  if (!dryRun) {
    try {
      execSync('git diff --cached --quiet', { cwd: projectDir });
      logger.info(chalk.gray('No new changes to commit (already committed)'));
    } catch {
      execCommand('git commit -m "chore: initial SmartStack project setup"', projectDir, dryRun);
    }
  } else {
    execCommand('git commit -m "chore: initial SmartStack project setup"', projectDir, dryRun);
  }
}

export const initCommand = new Command('init')
  .description('Initialize a new SmartStack project')
  .argument('[name]', 'Project name (optional, uses current folder if not provided)')
  .option('--dry-run', 'Show what would be created without actually creating')
  .option('-y, --yes', 'Skip prompts and use defaults')
  .option('--preview', 'Use preview/prerelease versions (NuGet --prerelease + npm @next)')
  .option('--here', 'Initialize in current directory (use folder name as project name)')
  .action(async (name: string | undefined, options: InitOptions) => {
    logger.header('SmartStack Project Initialization');

    // Check prerequisites
    let prereqs = checkPrerequisites();

    const MIN_DOTNET_MAJOR = 10;

    const dotnetMissing = !prereqs.dotnet;
    const dotnetMajor = parseInt(prereqs.dotnetVersion?.split('.')[0] ?? '0', 10);
    const dotnetOutdated = prereqs.dotnet && dotnetMajor < MIN_DOTNET_MAJOR;

    const DOTNET_DOWNLOAD_URL = `https://dotnet.microsoft.com/download/dotnet/${MIN_DOTNET_MAJOR}.0`;
    const DOCS_URL = 'https://docs.smartstack.app';

    const showInstallHelp = () => {
      logger.info('');
      logger.info(`Documentation:  ${chalk.cyan(DOCS_URL)}`);
      logger.info(`Download .NET:  ${chalk.cyan(DOTNET_DOWNLOAD_URL)}`);
    };

    if (dotnetMissing || dotnetOutdated) {
      if (dotnetMissing) {
        logger.error('.NET SDK not found.');
      } else {
        logger.error(`.NET SDK ${prereqs.dotnetVersion} detected — version ${MIN_DOTNET_MAJOR}.0 or later is required.`);
      }

      // On Windows, offer automatic installation via winget
      if (process.platform === 'win32') {
        const wingetCmd = `winget install Microsoft.DotNet.SDK.${MIN_DOTNET_MAJOR}`;

        if (!options.yes) {
          const { installChoice } = await inquirer.prompt([{
            type: 'list',
            name: 'installChoice',
            message: `Install .NET SDK ${MIN_DOTNET_MAJOR}.0 via winget?`,
            choices: [
              { name: `Yes — run: ${wingetCmd}`, value: 'winget' },
              { name: 'No — I will install it manually', value: 'manual' },
            ],
          }]);

          if (installChoice === 'winget') {
            logger.info(`Running: ${chalk.cyan(wingetCmd)}`);
            try {
              execSync(wingetCmd, { stdio: 'inherit', timeout: 300000 });

              // Re-check after installation
              const recheck = checkPrerequisites();
              const recheckMajor = parseInt(recheck.dotnetVersion?.split('.')[0] ?? '0', 10);
              if (recheck.dotnet && recheckMajor >= MIN_DOTNET_MAJOR) {
                logger.success(`.NET SDK ${recheck.dotnetVersion} installed successfully`);
                prereqs.dotnet = true;
                prereqs.dotnetVersion = recheck.dotnetVersion;
              } else {
                logger.error('Installation completed but .NET SDK still not detected.');
                logger.info('You may need to restart your terminal, then run ss init again.');
                showInstallHelp();
                process.exitCode = 1; return;
              }
            } catch {
              logger.error('winget installation failed.');
              showInstallHelp();
              process.exitCode = 1; return;
            }
          } else {
            logger.info('');
            logger.info(`Install manually: ${chalk.cyan(wingetCmd)}`);
            showInstallHelp();
            process.exitCode = 1; return;
          }
        } else {
          // --yes mode: attempt winget automatically
          logger.info(`Installing .NET SDK ${MIN_DOTNET_MAJOR}.0 via winget...`);
          try {
            execSync(`winget install Microsoft.DotNet.SDK.${MIN_DOTNET_MAJOR}`, { stdio: 'inherit', timeout: 300000 });
            const recheck = checkPrerequisites();
            const recheckMajor = parseInt(recheck.dotnetVersion?.split('.')[0] ?? '0', 10);
            if (recheck.dotnet && recheckMajor >= MIN_DOTNET_MAJOR) {
              logger.success(`.NET SDK ${recheck.dotnetVersion} installed successfully`);
              prereqs.dotnet = true;
              prereqs.dotnetVersion = recheck.dotnetVersion;
            } else {
              logger.error('Installation completed but .NET SDK still not detected.');
              logger.info('Restart your terminal, then run ss init again.');
              showInstallHelp();
              process.exitCode = 1; return;
            }
          } catch {
            logger.error('Automatic installation failed.');
            showInstallHelp();
            process.exitCode = 1; return;
          }
        }
      } else {
        // Non-Windows: show download link and docs
        showInstallHelp();
        process.exitCode = 1; return;
      }
    } else {
      logger.success(`.NET SDK ${prereqs.dotnetVersion} detected`);
    }

    // Detect project context (handles --here and no name provided)
    const context = await detectProjectContext(name, options.here ?? false);
    const { projectName, projectDir, inPlace } = context;
    let resumeState: InitState | undefined;

    // Show detected project info when in-place
    if (inPlace) {
      logger.info(`Detected project: ${chalk.cyan(projectName)}`);
      logger.info(`Directory: ${chalk.cyan(projectDir)}`);

      // Validate in-place directory (supports resume)
      const inPlaceResult = await validateInPlaceDirectory(projectDir);
      if (inPlaceResult.error) {
        logger.error(inPlaceResult.error);
        process.exitCode = 1; return;
      }

      if (inPlaceResult.isResume) {
        // Resume mode - store state and show summary
        resumeState = inPlaceResult.existingState;
        displayResumeSummary(resumeState!);

        if (!options.yes) {
          const { confirmResume } = await inquirer.prompt([
            {
              type: 'confirm',
              name: 'confirmResume',
              message: 'Resume initialization from where it left off?',
              default: true,
            },
          ]);

          if (!confirmResume) {
            logger.info('Cancelled by user.');
            return;
          }
        }
      } else {
        // Fresh init in-place - confirm with user
        if (!options.yes) {
          const { confirm } = await inquirer.prompt([
            {
              type: 'confirm',
              name: 'confirm',
              message: `Initialize SmartStack project "${projectName}" in current directory?`,
              default: true,
            },
          ]);

          if (!confirm) {
            logger.info('Cancelled by user.');
            return;
          }
        }
      }
    } else {
      // Traditional mode: create new directory
      if (await fs.pathExists(projectDir)) {
        logger.error(`Directory '${projectDir}' already exists.`);
        logger.info(`Tip: Use ${chalk.cyan('--here')} to initialize in the current directory instead.`);
        process.exitCode = 1; return;
      }
    }

    // Validate project name for C# namespace compatibility
    let finalProjectName = projectName;
    let finalProjectDir = projectDir;
    const validation = validateCSharpNamespace(projectName);
    if (!validation.valid) {
      logger.warning(`Project name "${projectName}" contains invalid characters for C# namespace.`);

      if (validation.suggestion && !options.yes) {
        // Ask user if they want to use the sanitized name
        const { useSuggestion } = await inquirer.prompt([
          {
            type: 'confirm',
            name: 'useSuggestion',
            message: `Use "${chalk.cyan(validation.suggestion)}" as C# namespace instead?`,
            default: true,
          },
        ]);

        if (useSuggestion) {
          finalProjectName = validation.suggestion;
          logger.success(`Using "${finalProjectName}" as project name`);
        } else {
          logger.error(`Cannot continue with invalid project name.`);
          logger.info(`Tip: Rename folder to use only letters, digits, and underscores.`);
          process.exitCode = 1; return;
        }
      } else if (validation.suggestion && options.yes) {
        // Auto-convert in --yes mode
        finalProjectName = validation.suggestion;
        logger.info(`Auto-converted to "${finalProjectName}" for C# compatibility`);
      } else {
        logger.error(`Invalid project name: ${validation.error}`);
        process.exitCode = 1; return;
      }
    }

    // Recalculate projectDir if name changed and not in-place mode
    if (!inPlace && finalProjectName !== projectName) {
      const cwd = process.cwd();
      finalProjectDir = isAbsolute(finalProjectName) ? finalProjectName : join(cwd, finalProjectName);
      logger.info(`Project directory updated to: ${chalk.cyan(finalProjectDir)}`);

      // Check if new directory already exists
      if (await fs.pathExists(finalProjectDir)) {
        logger.error(`Directory '${finalProjectDir}' already exists.`);
        process.exitCode = 1; return;
      }
    }

    let config: ProjectConfig;

    if (options.yes) {
      // Use defaults
      config = {
        name: finalProjectName,
        nameLower: finalProjectName.toLowerCase(),
        database: 'sqlserver',
        preview: options.preview ?? false,
        projectDir: finalProjectDir,
        email: {
          provider: 'Development',
          setupMailPit: false,
        },
      };
    } else {
      // Email provider configuration
      const emailAnswers = await inquirer.prompt([
        {
          type: 'list',
          name: 'emailProvider',
          message: 'Email provider:',
          choices: [
            { name: 'Development (MailPit - local SMTP testing)', value: 'Development' },
            { name: 'Mailgun', value: 'Mailgun' },
            { name: 'Azure Communication Services', value: 'AzureAcs' },
            { name: 'Disabled', value: 'Disabled' },
          ],
          default: 'Development',
        },
        {
          type: 'confirm',
          name: 'setupMailPit',
          message: 'Start MailPit Docker container for email testing?',
          default: true,
          when: (a) => a.emailProvider === 'Development' && isDockerAvailable(),
        },
      ]);

      config = {
        name: finalProjectName,
        nameLower: finalProjectName.toLowerCase(),
        database: 'sqlserver',
        preview: options.preview ?? false,
        projectDir: finalProjectDir,
        email: {
          provider: emailAnswers.emailProvider || 'Development',
          setupMailPit: emailAnswers.setupMailPit ?? false,
        },
      };
    }

    const dryRun = options.dryRun || false;

    if (dryRun) {
      logger.warning('DRY RUN MODE - No files will be created');
    }

    console.log();
    logger.info(`Project: ${chalk.cyan(config.name)}`);
    logger.info(`Database: ${chalk.cyan('SQL Server')}`);
    logger.info(`Preview mode: ${config.preview ? chalk.yellow('Enabled (prerelease)') : chalk.gray('Disabled (stable)')}`);
    logger.info(`Multi-Tenant: ${chalk.green('Enabled')} (personal tenant)`);
    const emailLabel = config.email.provider === 'Development'
      ? `${chalk.green('Development')} (MailPit - SMTP :1025, Web :8025)`
      : config.email.provider === 'Disabled'
        ? chalk.gray('Disabled')
        : chalk.cyan(config.email.provider);
    logger.info(`Email: ${emailLabel}`);
    console.log();

    try {
      // Get CLI version for state tracking
      let cliVersion = '0.0.0';
      try {
        // dirname(__dirname) = package root (dist/ → root), consistent with TEMPLATES_DIR
        const pkgPath = join(dirname(__dirname), 'package.json');
        const pkg = JSON.parse(await fs.readFile(pkgPath, 'utf-8'));
        cliVersion = pkg.version || '0.0.0';
      } catch { /* ignore */ }

      // Create or load init state for resume tracking
      const state: InitState = resumeState
        ?? createFreshInitState(config as unknown as Record<string, unknown>, cliVersion);

      // Create project directory (skip in in-place mode - directory already exists)
      if (!dryRun && !inPlace) {
        await fs.ensureDir(finalProjectDir);
      }

      // Ensure .smartstack directory exists before first step (needed to save state)
      if (!dryRun) {
        await fs.ensureDir(join(finalProjectDir, '.smartstack'));
        await saveInitState(finalProjectDir, state);
      }

      // Resolve SmartStack NuGet version before creating config
      // This ensures .smartstack/config.json has the correct version from the start
      if (!config.smartStackVersion) {
        logger.info('Resolving SmartStack package version...');
        const resolvedVersion = await getLatestNuGetVersion('SmartStack', config.preview);
        if (resolvedVersion) {
          config.smartStackVersion = resolvedVersion;
          logger.info(`SmartStack version: ${chalk.cyan(resolvedVersion)}`);
        }
      }

      // Execute each step with checkpoint tracking
      await executeStep(state, 'config', finalProjectDir, dryRun, () =>
        createConfigFiles(config, state, dryRun));

      await executeStep(state, 'backend', finalProjectDir, dryRun, () =>
        createBackendStructure(config, state, dryRun));

      await executeStep(state, 'frontend', finalProjectDir, dryRun, () =>
        createFrontendStructure(config, state, dryRun));

      await executeStep(state, 'docker', finalProjectDir, dryRun, () =>
        createDockerFiles(config, state, dryRun));

      await executeStep(state, 'git', finalProjectDir, dryRun, () =>
        initializeGit(config, dryRun));

      await executeStep(state, 'ralph', finalProjectDir, dryRun, async () => {
        if (!dryRun) {
          const ralphResult = await installRalphConfig({ projectPath: finalProjectDir });
          if (ralphResult.success) {
            logger.success('Ralph configuration created at .ralph/');
            // Track ralph files in init-state
            const ralphFiles = [
              { path: '.ralph/ralph.config.yaml', abs: ralphResult.configPath },
              { path: '.ralph/.gitignore', abs: join(finalProjectDir, '.ralph', '.gitignore') },
              { path: '.ralph/README.md', abs: join(finalProjectDir, '.ralph', 'README.md') },
            ];
            for (const rf of ralphFiles) {
              if (await fs.pathExists(rf.abs)) {
                const content = await fs.readFile(rf.abs);
                recordFile(state, 'ralph', rf.path, computeHash(content));
              }
            }
          } else {
            logger.warning('Could not create Ralph configuration (non-critical)');
          }
        } else {
          logger.info('Would create .ralph/ directory with configuration');
        }
      });

      // Setup MailPit if requested
      if (config.email.setupMailPit && !dryRun) {
        console.log();
        await setupMailPitContainer();
      }

      // Save final state
      if (!dryRun) {
        await saveInitState(finalProjectDir, state);
      }

      // Success summary
      const projectNameLower = finalProjectName.toLowerCase();
      const summary = [
        chalk.green.bold('Project created successfully!'),
        '',
        `Location: ${chalk.cyan(finalProjectDir)}`,
        '',
        chalk.bold('Next steps:'),
        '',
        chalk.yellow('Backend:'),
        `  1. Configure connection string in ${chalk.cyan('appsettings.json')}`,
        `  2. Create migration: ${chalk.cyan(`dotnet ef migrations add InitialCreate -p src/${finalProjectName}.Infrastructure -s src/${finalProjectName}.Api`)}`,
        `  3. Apply migration: ${chalk.cyan(`dotnet ef database update -p src/${finalProjectName}.Infrastructure -s src/${finalProjectName}.Api`)}`,
        `  4. Run the API: ${chalk.cyan(`dotnet run --project src/${finalProjectName}.Api`)}`,
        '',
        chalk.yellow('Frontend:'),
        `  1. Navigate to frontend: ${chalk.cyan(`cd web/${projectNameLower}-web`)}`,
        `  2. Install dependencies: ${chalk.cyan('npm install')}`,
        `  3. Start dev server: ${chalk.cyan('npm run dev')}`,
        '',
        chalk.yellow('Docker:'),
        `  Build & run:    ${chalk.cyan('docker compose -f docker-images/docker-compose.yml up --build')}`,
        `  Build backend:  ${chalk.cyan(`docker build -t smartstack-${projectNameLower}-backend:latest -f docker-images/Dockerfile.backend .`)}`,
        `  Build frontend: ${chalk.cyan(`docker build -t smartstack-${projectNameLower}-frontend:latest -f docker-images/Dockerfile.frontend .`)}`,
        '',
        chalk.yellow('Ralph (AI Automation):'),
        `  1. Verify Ralph setup: ${chalk.cyan('smartstack ralph start')}`,
        `  2. Start development: ${chalk.cyan('/ba-develop "Your feature"')}`,
        '',
        chalk.bold('Documentation:'),
        `  SmartStack: ${chalk.cyan('https://docs.smartstack.app')}`,
        `  API Reference: ${chalk.cyan('http://localhost:5142/scalar')} (when running)`,
        `  Frontend: ${chalk.cyan('http://localhost:3000')} (when running)`,
      ];

      logger.box(summary, 'success');
    } catch (error) {
      logger.error(`Failed to create project: ${error instanceof Error ? error.message : error}`);
      console.log();
      logger.info(`Fix the issue and re-run: ${chalk.cyan('ss init --here')}`);
      logger.info('Your progress has been saved and will be resumed automatically.');
      process.exitCode = 1; return;
    }
  });
